// Resource Gantt — bucket + stack 용량 뷰 캔버스 렌더러 (스펙 Next steps #1)
//
// - 행 = 리소스, X축 = 시간 버킷. 버킷마다 아이템이 바닥부터 stack.
// - 스택 위 ~ capacity 라인까지는 남은(여유) 용량(빈 영역).
// - 절대 스케일: 레인별 고정 기준값으로 버킷을 비교 가능하게 그린다.
//   각 버킷의 capacity 라인을 따로 긋고, 초과분(sum > cap)은 cap 라인 위로
//   빨갛게 튀어나오게 표시한다.
// - 성능: 보이는 행 + 시간창만 그리는 행 가상화. 모든 요소가 사각형이라
//   세그먼트당 fillRect 한 번으로 캔버스에 그린다.
//
// 좌측 트리(AG Grid)·히트테스트·실데이터 연동은 후속 단계에서 붙인다.
// 지금은 좌측에 임시 리소스 라벨만 그린다.

import { useRef, useEffect, useMemo, useCallback } from "react";

import Box from "@mui/material/Box";

import {
  GANTT_DEFAULTS,
  getPaletteSetupColor,
  CAP_LINE_COLOR,
  LIMIT_LINE_COLOR,
  EMPTY_FILL,
  OVERLOAD_COLOR,
  ROW_DIVIDER_COLOR,
  LANE_CODE_COLOR,
  GRIDLINE_COLOR,
  BUCKET_HEADER_BG,
  HEADER_FONT,
  LANE_NAME_FONT,
  LANE_CODE_FONT,
  SEGMENT_FONT,
  LABEL_COLOR,
  BORDER_COLOR,
  BODY_BG,
  LANE_BG,
  HEADER_TEXT_COLOR,
  LANE_SEP_COLOR,
  SELECTION_HALO,
  SELECTION_OUTLINE,
  SELECTION_ACCENT,
  SELECTION_FILL,
  LINK_ARROW_LEN,
  LINK_ARROW_SPREAD,
  NONWORKING_BG,
  NONWORKING_HATCH_COLOR,
  NONWORKING_HATCH_WIDTH,
  NONWORKING_HATCH_GAP,
  SEGMENT_DIM,
  loadColor,
  isOverloaded,
  loadColorRatio,
} from "@plannel/pages/master-plan/resource-gantt/ganttConstants";
import {
  bucketCap,
  bucketLimitCap,
  buildCapacityScale,
  buildItemColor,
  computeLaneRef,
  stackBucket,
  stackTotal,
  LANE_HEADROOM,
} from "@plannel/pages/master-plan/resource-gantt/ganttScales";

// 정수 좌표로 그려 안티앨리어싱 번짐을 줄인다(후속 color-picking과도 호환).
const px = (v) => Math.round(v);

// 2단(연도+날짜) 헤더의 하단 라벨 — 라벨이 연도로 시작하면 제거("2026-02-23" → "02/23").
// 주 라벨("02/23 W09")처럼 이미 연도가 없으면 그대로 둔다. key = ISO 날짜("2026-02-23").
const stripYearLabel = (label, key) => {
  const s = String(label ?? "");
  const year = String(key).slice(0, 4);
  if (!s.startsWith(year)) return s;
  const rest = s.slice(4).replace(/^[-./\s]+/, "");
  return rest ? rest.replace(/-/g, "/") : s;
};

// day 버킷 라벨 뒤에 붙일 요일 인덱스(0=일 ~ 6=토) — key("YYYY-MM-DD") 기준. (ZDate는 전역)
const weekdayIdx = (key) => {
  const day = new ZDate(key).getDay();
  return Number.isNaN(day) ? -1 : day;
};
// 요일 라벨 기본값 — 페이지에서 lang-pack(t("sun")~t("sat"))으로 넘겨주면 그것을 쓴다.
const DEFAULT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// 채움색 위에서 읽히는 텍스트색 — 밝으면 진한 글자, 어두우면 흰 글자.
const textColorOn = (hex) => {
  const h = String(hex).replace("#", "");
  if (h.length < 6) return "#1a1a1a";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#1a1a1a" : "#ffffff";
};

const ResourceGanttCanvas = ({
  resources = [],
  bucketsByResource = {},
  bucketKeys = [],
  bucketLabels = {}, // { key: 표시라벨 } — 없으면 key 그대로
  laneHeaderLabel = "", // 좌측 상단 코너(리소스 컬럼) 헤더 라벨
  emptyLabel = "", // 표시할 리소스가 없을 때 본문에 띄우는 문구(그리드의 no-rows 오버레이 역할)
  monthLabels = [], // 월 병합 행 라벨 12개(1월~12월) — lang-pack 값
  // 이 리소스·버킷이 보이도록 스크롤/패닝을 옮긴다. 화면 밖일 때만 움직이고, 매번 새 객체를
  // 넘겨야 같은 대상을 다시 눌러도 동작한다. { resourceId, bucketKey }
  revealTarget = null,
  selected, // 선택된 세그먼트 { resource, bucket, item } — 강조 외곽선 표시
  isolateOrderNo, // 이 주문만 강조하고 나머지는 회색 처리(우클릭 '같은 주문 강조')
  colorForItem: colorForItemProp, // 아이템→색 (없으면 내부 생성). 패널과 색 공유용
  loadColorFn = loadColor, // 부하율→색 (임계값 반영). 미지정 시 기본 임계값
  colorMode = "item", // "item": 아이템별 색 / "load": 부하율 색
  stackDir = "vertical", // "vertical": 아래→위 / "horizontal": 좌→우 스택
  fitCapacity = false, // true: 초과 표시 여백 제거 → 100%면 버킷이 꽉 참
  setupColor = getPaletteSetupColor(), // setup(작업 준비) 구간 색 — 현재 팔레트에서 주입(미지정 시 기본 팔레트)
  showSetup = true, // false 면 setup 구간 오버레이를 그리지 않는다(capacity 에 포함된 값이라 막대 길이는 불변)
  weekdayLabels = DEFAULT_WEEKDAYS, // day 버킷 헤더 요일 라벨(일~토) — lang-pack 적용값 주입

  onSegmentClick, // (hit) => void. hit = { resource, bucket, item } | null
  onBucketContextMenu, // (hit, clientX, clientY) => void. hit = { resource, bucket }
  options = {},
}) => {
  const cfg = useMemo(() => ({ ...GANTT_DEFAULTS, ...options }), [options]);

  // 용량축 기준은 리소스 전체(행 최대 capacity) 고정 — 버킷마다 스케일이 달라지면 행 안에서
  // 막대 높이를 서로 비교할 수 없다.
  const resolveLaneRef = useCallback(
    (buckets, resourceMaxCapacity) => computeLaneRef(buckets, resourceMaxCapacity, fitCapacity ? 0 : LANE_HEADROOM),
    [fitCapacity]
  );

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const rafRef = useRef(0);
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });
  const panRef = useRef(0); // 가로 패닝 오프셋(px). 드래그로 변경
  const revealedRef = useRef(null); // 마지막으로 처리한 revealTarget — 같은 요청을 두 번 처리하지 않는다
  const dragRef = useRef({ active: false, startX: 0, startPan: 0, moved: false });

  const colorForItem = useMemo(
    () => colorForItemProp ?? buildItemColor(bucketsByResource),
    [colorForItemProp, bucketsByResource]
  );

  // 버킷 key → 인덱스 (가로 위치 계산용)
  const idxOf = useMemo(() => {
    const m = new Map();
    bucketKeys.forEach((k, i) => m.set(k, i));
    return m;
  }, [bucketKeys]);

  // 가로 메트릭(버킷 폭/컨텐츠 폭/최대 패닝) — draw·드래그·히트테스트 공용
  const horizMetrics = useCallback(() => {
    const { width } = sizeRef.current;
    const { laneLeft, bucketStandardCount } = cfg;
    const n = bucketKeys.length;
    const viewportW = Math.max(0, width - laneLeft);
    // 표준 개수(기본 12)가 뷰포트를 꽉 채우는 폭을 고정 슬롯 폭으로 사용.
    // 정수로 내린다 — 소수 폭이면 i * bucketW 가 버킷마다 다른 서브픽셀 오프셋을 만들어
    // 라벨이 열마다 다른 위치에 렌더되고, 드래그할 때마다 흔들려 보인다.
    // (오른쪽에 최대 몇 px 여백이 남는 것은 감수한다.)
    const bucketW = viewportW > 0 ? Math.max(1, Math.floor(viewportW / (bucketStandardCount || 12))) : 80;
    const contentW = n * bucketW;
    return { viewportW, bucketW, contentW, maxPan: Math.max(0, contentW - viewportW) };
  }, [cfg, bucketKeys]);

  const totalHeight = cfg.headerHeight + resources.length * cfg.rowHeight;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const scroller = scrollRef.current;
    if (!canvas || !scroller) return;

    const ctx = canvas.getContext("2d");
    const { width, height } = sizeRef.current;
    if (!width || !height) return;

    const { headerHeight, rowHeight, rowPaddingY, laneLeft, capLineWidth, bucketGap } = cfg;

    // CSS 픽셀 좌표계로 그린다(고DPI는 transform 으로 흡수).
    // 배율은 devicePixelRatio 가 아니라 '실제 버퍼 크기 / CSS 크기'로 구한다 — 버퍼 크기는 정수로
    // 반올림되므로 dpr 을 그대로 쓰면 미세하게 어긋나고, 그 어긋남이 글자를 서브픽셀 단위로
    // 밀어 흔들려 보이게 한다(Windows 125%/150% 배율에서 특히).
    ctx.setTransform(canvas.width / width, 0, 0, canvas.height / height, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, width, height);
    // 그리드 viewport 처럼 본문 배경을 직접 칠한다 — 안 칠하면 페이지 배경이 비쳐 경계가 흐려진다.
    ctx.fillStyle = BODY_BG;
    ctx.fillRect(0, 0, width, height);

    // 스크롤·패닝 양을 정수로 고정한다. 브라우저가 주는 scrollTop 은 고DPI/스무스 스크롤에서
    // 소수가 되고, 그러면 행 좌표가 프레임마다 미세하게 달라져 텍스트가 떨린다.
    const scrollTop = Math.round(scroller.scrollTop);

    // 가로 레이아웃 — 버킷 고정 폭. 넘치면 패닝(드래그)으로 본다.
    const { viewportW, bucketW, maxPan } = horizMetrics();
    const pan = Math.round(Math.min(Math.max(0, panRef.current), maxPan));
    panRef.current = pan;
    const barW = px(bucketW - bucketGap);
    const slotLeft = (i) => laneLeft + i * bucketW - pan; // 슬롯 좌 화면 x
    const barLeftOf = (i) => px(slotLeft(i) + bucketGap / 2);

    // 행이 끝나는 화면 y — 마지막 행 아래는 빈 배경이다(그리드도 행 없는 영역엔 선을 안 긋는다).
    const bodyBottom = Math.max(
      headerHeight,
      Math.min(height, headerHeight + resources.length * rowHeight - scrollTop)
    );

    // 보이는 행 범위만 계산(행 가상화).
    const first = Math.max(0, Math.floor(scrollTop / rowHeight));
    const last = Math.min(
      resources.length - 1,
      Math.ceil((scrollTop + height - headerHeight) / rowHeight)
    );

    // 같은 주문(orderNo)이면 리소스/버킷 무관하게 모두 강조(한 주문이 분할될 수 있음).
    // 같은 아이템 코드라도 주문이 다르면 강조하지 않는다.
    // 무주문 생산(order_cd null → orderNo null)은 주문으로 묶을 수 없으므로
    // 클릭한 그 세그먼트만(리소스+버킷+품목) 강조한다.
    const selectedOrderNo = selected?.item?.orderNo;
    const selectedSegKey =
      selectedOrderNo == null && selected?.item
        ? `${selected.resource?.id}|${selected.bucket?.key}|${selected.item.item}`
        : null;
    // 선택 강조 대상 — 주문번호가 있으면 리소스/버킷 무관하게 같은 주문 전부, 무주문이면 그 세그먼트만
    const isSelectedSeg = (resId, bucketKey, seg) =>
      selectedOrderNo != null
        ? seg.orderNo === selectedOrderNo
        : selectedSegKey != null && selectedSegKey === `${resId}|${bucketKey}|${seg.item}`;
    const selRects = [];
    // isolate(같은 주문 강조) 모드에서 그 주문 세그먼트의 중심점 — 계획 순서를 선으로 잇는다.
    // bi(버킷 인덱스) / ri(리소스 행) / si(스택 누적 시작값 = seg.y0)를 정렬 키로 함께 담는다.
    const orderLinkPts = [];

    // ── Layer A: 차트 영역(버킷) — 좌측 레인 침범 방지 위해 가로 클립 ──
    ctx.save();
    ctx.beginPath();
    ctx.rect(laneLeft, headerHeight, viewportW, height - headerHeight);
    ctx.clip();

    // 버킷 세로 가이드라인
    ctx.fillStyle = GRIDLINE_COLOR;
    for (let i = 0; i < bucketKeys.length; i++) {
      const gx = slotLeft(i);
      if (gx >= laneLeft - 1 && gx <= width) ctx.fillRect(px(gx), headerHeight, 1, bodyBottom - headerHeight);
    }

    for (let r = first; r <= last && r >= 0; r++) {
      const res = resources[r];
      if (!res) continue;

      const rowTopAbs = headerHeight + r * rowHeight - scrollTop;
      const rowTop = rowTopAbs + rowPaddingY;
      const rowBottom = rowTopAbs + rowHeight - rowPaddingY;
      const buckets = bucketsByResource[res.id] || [];

      ctx.fillStyle = ROW_DIVIDER_COLOR;
      ctx.fillRect(laneLeft, px(rowTopAbs + rowHeight) - 0.5, viewportW, 1);

      const rowLaneRef = resolveLaneRef(buckets, res.maxCapacity);
      const rowScale = buildCapacityScale(rowLaneRef, rowTop, rowBottom);

      for (const b of buckets) {
        const i = idxOf.get(b.key);
        if (i == null) continue;
        const left = barLeftOf(i);
        if (left > width || left + barW < laneLeft) continue; // 화면 밖 컬

        const stacked = stackBucket(b.items ?? []);
        const total = stackTotal(stacked);
        const cap = bucketCap(b);
        // 초과 판정 기준 = 한계 용량(총능력 × usageLimitRate). 표시 숫자·cap 라인 위치는 총능력 기준 유지.
        const limitCap = bucketLimitCap(b);
        const w = barW;
        const laneRef = rowLaneRef;
        const y = rowScale; // 용량축 스케일 — 행(리소스) 기준 하나뿐이다

        // 비가동 버킷(총능력 0) — 빗금으로 표시한다. 부하 0 인 가동일과 색만으로는 구분이 안 된다.
        // 막대 영역은 스택 방향과 무관하게 같다(left..left+w / rowTop..rowBottom).
        // 부하가 있는 비가동 버킷(초과)도 있으므로 세그먼트보다 먼저 깔고 그 위에 그린다.
        if (cap <= 0) {
          const nwTop = px(rowTop);
          const nwH = px(rowBottom) - nwTop;
          // 배경은 본문색(흰색)으로 둔다 — 부하 0 인 가동 버킷은 EMPTY_FILL 단색으로 채워지므로,
          // '단색 회색 = 여유' vs '흰 바탕 + 빗금 = 비가동' 으로 갈라 보이게 하는 것이 목적이다.
          ctx.fillStyle = NONWORKING_BG;
          ctx.fillRect(left, nwTop, w, nwH);

          // 좌표를 막대 기준으로 잡는다 — createPattern 은 캔버스에 고정돼 패닝하면 빗금만 밀려 보인다.
          ctx.save();
          ctx.beginPath();
          ctx.rect(left, nwTop, w, nwH);
          ctx.clip();
          ctx.strokeStyle = NONWORKING_HATCH_COLOR;
          ctx.lineWidth = NONWORKING_HATCH_WIDTH;
          ctx.beginPath();
          for (let d = 0; d <= w + nwH; d += NONWORKING_HATCH_GAP) {
            ctx.moveTo(left + d, nwTop);
            ctx.lineTo(left + d - nwH, nwTop + nwH);
          }
          ctx.stroke();
          ctx.restore();

          // 빗금 블록의 경계 — 흰 바탕이라 테두리가 없으면 어디까지가 비가동인지 안 보인다.
          ctx.strokeStyle = NONWORKING_HATCH_COLOR;
          ctx.lineWidth = 1;
          ctx.strokeRect(px(left) + 0.5, nwTop + 0.5, px(w) - 1, nwH - 1);
        }

        if (stackDir === "horizontal") {
          // 가로 스택: 용량축 = x(left..left+w), 막대 두께 = 행 높이. cap 라인은 세로.
          const barTop = px(rowTop);
          const barH = px(rowBottom) - barTop;
          // 여유(laneRef)를 넘는 초과분은 막대 끝에 고정(clamp) → 옆 버킷으로 안 넘침
          const xOf = (v) => left + Math.min(laneRef ? v / laneRef : 0, 1) * w;

          for (const seg of stacked) {
            const x0 = px(xOf(seg.y0));
            const x1 = px(xOf(seg.y1));
            const segW = x1 - x0;
            const dim = isolateOrderNo && seg.orderNo !== isolateOrderNo;
            const fill = dim ? SEGMENT_DIM : colorMode === "load" ? loadColorFn(loadColorRatio(total, limitCap)) : colorForItem(seg.item);
            // capacity 안에 포함된 setup 몫(세그먼트 범위로 clamp). 숨기면 0 → 오버레이·텍스트 배치 모두 일반 세그먼트와 같아진다.
            const setup = showSetup ? Math.min(seg.setupCapacity || 0, seg.y1 - seg.y0) : 0;
            const setupX1 = setup > 0 ? px(xOf(seg.y0 + setup)) : x0;
            const setupW = setupX1 - x0; // setup 구간 폭
            const mainX0 = setupX1;   // 메인 구간 시작 x
            const mainW = x1 - setupX1; // 메인 구간 폭
            // 메인 구간
            ctx.fillStyle = fill;
            ctx.fillRect(x0, barTop, segW, barH);
            // setup 구간 오버레이 (왼쪽) — capacity 안쪽 구간이라 스택 높이는 그대로다.
            // 라벨 없이 색으로만 구분한다(좁은 구간에 글자가 들어가면 지저분해서).
            if (!dim && setup > 0) {
              ctx.fillStyle = setupColor;
              ctx.fillRect(x0, barTop, setupW, barH);
            }
            if (isSelectedSeg(res.id, b.key, seg) || (isolateOrderNo && seg.orderNo === isolateOrderNo))
              selRects.push({ left: x0, top: barTop, w: segW, h: barH });
            if (isolateOrderNo && seg.orderNo === isolateOrderNo)
              orderLinkPts.push({ x: x0 + segW / 2, y: barTop + barH / 2, bi: i, ri: r, si: seg.y0 });
            // 아이템 텍스트 — 메인 구간 중앙에만 배치해 setup 텍스트와 겹침 방지
            if (mainW >= 30 && barH >= 14) {
              ctx.fillStyle = textColorOn(fill);
              ctx.font = SEGMENT_FONT;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle"; // 이전 레이어에서 남은 값에 의존하지 않는다
              ctx.fillText(seg.item, px(mainX0 + mainW / 2), px(barTop + barH / 2), mainW - 6);
              ctx.textAlign = "left";
            }
          }

          if (colorMode === "load" && stacked.length > 1) {
            ctx.fillStyle = "#ffffff66";
            for (let k = 0; k < stacked.length - 1; k++) ctx.fillRect(px(xOf(stacked[k].y1)), barTop, 1, barH);
          }

          if (total < cap) {
            const ex0 = px(xOf(total));
            ctx.fillStyle = EMPTY_FILL;
            ctx.fillRect(ex0, barTop, px(xOf(cap)) - ex0, barH);
          }

          const capX = px(xOf(cap)) + 0.5;
          ctx.strokeStyle = isOverloaded(total, cap) ? OVERLOAD_COLOR : CAP_LINE_COLOR;
          ctx.lineWidth = capLineWidth;
          ctx.setLineDash([6, 4]);
          ctx.beginPath();
          ctx.moveTo(capX, barTop);
          ctx.lineTo(capX, barTop + barH);
          ctx.stroke();
          ctx.setLineDash([]);

          // 사용 한도 보조 라인 — 초과 판정 기준선. 비율 100%면 cap 라인과 겹치므로 그리지 않는다.
          if (limitCap !== cap) {
            const limitX = px(xOf(limitCap)) + 0.5;
            ctx.strokeStyle = isOverloaded(total, limitCap) ? OVERLOAD_COLOR : LIMIT_LINE_COLOR;
            ctx.lineWidth = capLineWidth;
            ctx.setLineDash([3, 4]);
            ctx.beginPath();
            ctx.moveTo(limitX, barTop);
            ctx.lineTo(limitX, barTop + barH);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        } else {
          // 세로 스택(현재): 용량축 = y, 막대 폭 = 버킷 폭. cap 라인은 가로.
          for (const seg of stacked) {
            const top = px(y(seg.y1));
            const bottom = px(y(seg.y0));
            const segH = bottom - top;
            const dim = isolateOrderNo && seg.orderNo !== isolateOrderNo;
            const fill = dim ? SEGMENT_DIM : colorMode === "load" ? loadColorFn(loadColorRatio(total, limitCap)) : colorForItem(seg.item);
            // capacity 안에 포함된 setup 몫(세그먼트 범위로 clamp). 숨기면 0 → 오버레이·텍스트 배치 모두 일반 세그먼트와 같아진다.
            const setup = showSetup ? Math.min(seg.setupCapacity || 0, seg.y1 - seg.y0) : 0;
            const setupTop = setup > 0 ? px(y(seg.y0 + setup)) : bottom;
            const setupH = bottom - setupTop; // setup 구간 높이
            const mainH = setupTop - top;  // 메인 구간 높이
            // 메인 구간
            ctx.fillStyle = fill;
            ctx.fillRect(left, top, w, segH);
            // setup 구간 오버레이 (하단) — capacity 안쪽 구간이라 스택 높이는 그대로다.
            // 라벨 없이 색으로만 구분한다(좁은 구간에 글자가 들어가면 지저분해서).
            if (!dim && setup > 0) {
              ctx.fillStyle = setupColor;
              ctx.fillRect(left, setupTop, w, setupH);
            }
            if (isSelectedSeg(res.id, b.key, seg) || (isolateOrderNo && seg.orderNo === isolateOrderNo))
              selRects.push({ left, top, w, h: segH });
            if (isolateOrderNo && seg.orderNo === isolateOrderNo)
              orderLinkPts.push({ x: left + w / 2, y: top + segH / 2, bi: i, ri: r, si: seg.y0 });
            // 아이템 텍스트 — 메인 구간 중앙에만 배치해 setup 텍스트와 겹침 방지
            if (mainH >= 14 && w >= 30) {
              ctx.fillStyle = textColorOn(fill);
              ctx.font = SEGMENT_FONT;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle"; // 이전 레이어에서 남은 값에 의존하지 않는다
              ctx.fillText(seg.item, px(left + w / 2), px(top + mainH / 2), w - 6);
              ctx.textAlign = "left";
            }
          }

          if (colorMode === "load" && stacked.length > 1) {
            ctx.fillStyle = "#ffffff66";
            for (let k = 0; k < stacked.length - 1; k++) ctx.fillRect(left, px(y(stacked[k].y1)), w, 1);
          }

          if (total < cap) {
            const top = px(y(cap));
            ctx.fillStyle = EMPTY_FILL;
            ctx.fillRect(left, top, w, px(y(total)) - top);
          }

          const capY = px(y(cap)) + 0.5;
          ctx.strokeStyle = isOverloaded(total, cap) ? OVERLOAD_COLOR : CAP_LINE_COLOR;
          ctx.lineWidth = capLineWidth;
          ctx.setLineDash([6, 4]);
          ctx.beginPath();
          ctx.moveTo(left, capY);
          ctx.lineTo(left + w, capY);
          ctx.stroke();
          ctx.setLineDash([]);

          // 사용 한도 보조 라인 — 초과 판정 기준선. 비율 100%면 cap 라인과 겹치므로 그리지 않는다.
          if (limitCap !== cap) {
            const limitY = px(y(limitCap)) + 0.5;
            ctx.strokeStyle = isOverloaded(total, limitCap) ? OVERLOAD_COLOR : LIMIT_LINE_COLOR;
            ctx.lineWidth = capLineWidth;
            ctx.setLineDash([3, 4]);
            ctx.beginPath();
            ctx.moveTo(left, limitY);
            ctx.lineTo(left + w, limitY);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      }
    }

    // 같은 주문 강조 모드 — 그 주문 세그먼트들을 계획 순서로 선으로 연결(주문 흐름 시각화).
    // 정렬 키는 화면 좌표가 아니라 데이터 순서다: 버킷(시간) → 리소스 행 → 스택 누적(seg.y0).
    // 스택은 바닥(가로 스택이면 왼쪽)부터 쌓이므로 y0 가 작을수록 먼저 실행되는 계획이고,
    // 세로 스택에서는 그게 화면상 아래쪽이다 → y 픽셀 오름차순으로 정렬하면 진행 방향이 거꾸로 된다.
    if (isolateOrderNo && orderLinkPts.length > 1) {
      const pts = orderLinkPts.slice().sort((a, b) => a.bi - b.bi || a.ri - b.ri || a.si - b.si);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      const trace = () => {
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y);
        ctx.stroke();
      };
      // 흰 헤일로 → 진한 점선 (어떤 색 위에서도 보이게). 같은 dash 패턴으로 겹쳐 그린다.
      ctx.setLineDash([2, 5]);
      ctx.strokeStyle = SELECTION_HALO;
      ctx.lineWidth = 4;
      trace();
      ctx.strokeStyle = SELECTION_OUTLINE;
      ctx.lineWidth = 2;
      trace();
      ctx.setLineDash([]);
      // 각 세그먼트 지점에 노드 점
      for (const p of pts) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = SELECTION_HALO;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = SELECTION_OUTLINE;
        ctx.fill();
      }
      // 진행 방향 화살촉 — 각 구간 중앙에서 다음 지점을 향해 그린다.
      // 점선만으로는 어느 쪽이 먼저인지 알 수 없어 방향을 눈에 보이게 한다.
      for (let k = 1; k < pts.length; k++) {
        const p0 = pts[k - 1];
        const p1 = pts[k];
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        if (Math.hypot(dx, dy) < LINK_ARROW_LEN * 2) continue; // 짧은 구간은 화살촉이 노드 점과 겹친다
        const ang = Math.atan2(dy, dx);
        const cx = p0.x + dx / 2;
        const cy = p0.y + dy / 2;
        const head = () => {
          ctx.beginPath();
          ctx.moveTo(cx - Math.cos(ang - LINK_ARROW_SPREAD) * LINK_ARROW_LEN, cy - Math.sin(ang - LINK_ARROW_SPREAD) * LINK_ARROW_LEN);
          ctx.lineTo(cx, cy);
          ctx.lineTo(cx - Math.cos(ang + LINK_ARROW_SPREAD) * LINK_ARROW_LEN, cy - Math.sin(ang + LINK_ARROW_SPREAD) * LINK_ARROW_LEN);
          ctx.stroke();
        };
        ctx.strokeStyle = SELECTION_HALO;
        ctx.lineWidth = 4;
        head();
        ctx.strokeStyle = SELECTION_OUTLINE;
        ctx.lineWidth = 2;
        head();
      }
    }

    // 선택 강조 — 선택색 채움 + 흰 헤일로 + 강조색 외곽선.
    // 외곽선만으로는 세그먼트가 작거나 색이 진할 때 묻혀서, 그리드 선택 행처럼 면을 함께 덮는다.
    for (const s of selRects) {
      ctx.fillStyle = SELECTION_FILL;
      ctx.fillRect(s.left, s.top, s.w, s.h);

      // 얇은 세그먼트에서 inset 이 폭/높이를 넘어 사각형이 뒤집히지 않게 제한한다.
      const ins = Math.max(0.5, Math.min(2, s.w / 4, s.h / 4));
      const rect = [s.left + ins, s.top + ins, Math.max(0, s.w - ins * 2), Math.max(0, s.h - ins * 2)];
      ctx.strokeStyle = SELECTION_HALO;
      ctx.lineWidth = 4;
      ctx.strokeRect(...rect);
      ctx.strokeStyle = SELECTION_ACCENT;
      ctx.lineWidth = 2.5;
      ctx.strokeRect(...rect);
    }
    ctx.restore();

    // ── Layer B: 좌측 리소스 컬럼(고정) ──
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, headerHeight, laneLeft, bodyBottom - headerHeight);
    ctx.clip();
    ctx.fillStyle = LANE_BG;
    ctx.fillRect(0, headerHeight, laneLeft, bodyBottom - headerHeight);
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    for (let r = first; r <= last && r >= 0; r++) {
      const res = resources[r];
      if (!res) continue;
      const rowTopAbs = headerHeight + r * rowHeight - scrollTop;
      ctx.fillStyle = ROW_DIVIDER_COLOR;
      ctx.fillRect(0, px(rowTopAbs + rowHeight) - 0.5, laneLeft, 1);

      const cy = rowTopAbs + rowHeight / 2;
      // 리소스명(굵게) + 리소스 코드(res.id=resCd, 옅은 회색). 코드가 이름과 같으면 한 줄만.
      // 그리드 기준으로 리소스명은 '셀 값'이라 헤더색(옅음)이 아니라 foreground 를 쓴다.
      const code = res.id && res.id !== res.name ? res.id : null;
      ctx.font = LANE_NAME_FONT;
      ctx.fillStyle = LABEL_COLOR;
      ctx.fillText(res.name ?? res.id, 8, px(code ? cy - 8 : cy), laneLeft - 12);
      if (code) {
        ctx.font = LANE_CODE_FONT;
        ctx.fillStyle = LANE_CODE_COLOR;
        ctx.fillText(code, 8, px(cy + 9), laneLeft - 12);
      }
    }
    ctx.restore();

    // 레인 ↔ 차트 경계선 — 행이 있는 구간까지만
    ctx.fillStyle = LANE_SEP_COLOR;
    ctx.fillRect(px(laneLeft) - 0.5, headerHeight, 1, bodyBottom - headerHeight);

    // 표시할 리소스가 없을 때 — 빈 캔버스로 두면 헤더만 떠 있어 미완성처럼 보인다.
    if (!resources.length && emptyLabel) {
      ctx.font = HEADER_FONT;
      ctx.fillStyle = HEADER_TEXT_COLOR;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(emptyLabel, px(laneLeft + viewportW / 2), px(headerHeight + (height - headerHeight) / 2));
      ctx.textAlign = "left";
    }

    // ── Layer C: 상단 헤더(스크롤 고정) ──
    ctx.fillStyle = BUCKET_HEADER_BG;
    ctx.fillRect(0, 0, width, headerHeight);

    // 다단 헤더: [연도] - [월] - [날짜]. 병합 행은 버킷 단위에 따라 켜진다.
    const yearRow = !!cfg.yearRow;
    const monthRow = !!cfg.monthRow;
    const yearH = yearRow ? 22 : 0;
    const monthH = monthRow ? 22 : 0;
    const dateH = headerHeight - yearH - monthH; // 남는 행 = 날짜(balham 헤더 높이 32px)
    const labelCy = yearH + monthH + dateH / 2; // 날짜 라벨 세로 중심

    ctx.save();
    ctx.beginPath();
    ctx.rect(laneLeft, 0, viewportW, headerHeight);
    ctx.clip();

    // 병합 행(연도·월) 렌더 — AG Grid 의 header group 처럼 가운데 정렬한다(App.scss 가 그리드
    // 헤더도 justify-content: center 로 맞춰 둠). 다만 그룹이 화면보다 넓거나 일부만 보일 수 있어
    // 그룹 전체가 아니라 '보이는 구간'의 중앙에 둔다 — 스크롤해도 라벨이 화면에 남는다.
    const drawGroupRow = (top, h, groupKeyOf, labelOf) => {
      ctx.font = HEADER_FONT;
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      let g = 0;
      while (g < bucketKeys.length) {
        const gk = groupKeyOf(bucketKeys[g]);
        let end = g;
        while (end + 1 < bucketKeys.length && groupKeyOf(bucketKeys[end + 1]) === gk) end += 1;
        const x0 = slotLeft(g);
        const x1 = slotLeft(end + 1);
        if (x1 >= laneLeft && x0 <= width) {
          // 그룹 경계 세로선(첫 그룹 제외)
          if (g > 0 && x0 >= laneLeft) {
            ctx.fillStyle = GRIDLINE_COLOR;
            ctx.fillRect(px(x0) - 0.5, top, 1, h);
          }
          const label = labelOf(gk);
          const tw = ctx.measureText(label).width;
          const visX0 = Math.max(x0, laneLeft);
          const visX1 = Math.min(x1, width);
          ctx.fillStyle = HEADER_TEXT_COLOR;
          if (visX1 - visX0 > tw + 12) ctx.fillText(label, px((visX0 + visX1) / 2), px(top + h / 2));
        }
        g = end + 1;
      }
      // 아래 행과의 구분선
      ctx.fillStyle = GRIDLINE_COLOR;
      ctx.fillRect(laneLeft, px(top + h) - 0.5, viewportW, 1);
    };

    if (yearRow) {
      drawGroupRow(0, yearH, (key) => String(key).slice(0, 4), (gk) => gk);
    }
    if (monthRow) {
      drawGroupRow(
        yearH,
        monthH,
        (key) => String(key).slice(0, 7), // YYYY-MM
        (gk) => {
          const mm = Number(String(gk).slice(5, 7));
          return monthLabels[mm - 1] ?? String(gk).slice(5, 7);
        }
      );
    }

    // 버킷 경계 구분선 — balham 의 header column separator(높이 50%, 세로 중앙)와 같은 모양.
    ctx.fillStyle = GRIDLINE_COLOR;
    const sepTop = yearH + monthH + dateH * 0.25;
    const sepH = dateH * 0.5;
    for (let i = 1; i < bucketKeys.length; i++) {
      const sx = slotLeft(i);
      if (sx < laneLeft || sx > width) continue;
      ctx.fillRect(px(sx) - 0.5, px(sepTop), 1, px(sepH));
    }

    // 버킷(시간축) 헤더 라벨 — 2단이면 연도 없이 날짜만
    ctx.font = HEADER_FONT;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillStyle = HEADER_TEXT_COLOR;
    for (let i = 0; i < bucketKeys.length; i++) {
      const cx = slotLeft(i) + bucketW / 2;
      if (cx < laneLeft - bucketW || cx > width + bucketW) continue;
      const key = bucketKeys[i];
      const raw = bucketLabels[key] ?? key;
      let label = raw;
      if (yearRow) {
        // day 버킷은 백엔드 라벨이 ISO 날짜 그대로(label === key) → "02/23 Mon" 형태로 요일을 붙인다.
        const isDay = raw === key;
        // 월 버킷 라벨은 "YYYY-MM"(DateUtil.headerDateFormatter). 위 월 병합 행이 이미 그 달을
        // 보여주므로 하단은 비운다 — 안 비우면 연도 / 월 / 월숫자로 3단처럼 보인다.
        // (멀티버킷 헤더에서는 열마다 단 수를 달리할 수 없어 하단 라벨 생략으로 처리한다.)
        if (monthRow && /^\d{4}-\d{2}$/.test(raw)) continue;
        label = stripYearLabel(raw, key);
        // 월이 위 행에 따로 있으면 하단은 '일'만 — 안 빼면 월이 두 번 보인다.
        if (monthRow) label = label.replace(/^\d{1,2}[/-]/, "");
        if (isDay) {
          const wi = weekdayIdx(key);
          if (wi >= 0 && weekdayLabels[wi]) label = `${label} ${weekdayLabels[wi]}`;
        }
      }
      ctx.fillText(label, px(cx), px(labelCy), bucketW - 6);
    }
    ctx.restore();

    // 코너(리소스 헤더) + 경계선
    ctx.fillStyle = LANE_SEP_COLOR;
    ctx.fillRect(0, headerHeight - 1, width, 1);
    ctx.fillRect(px(laneLeft) - 0.5, 0, 1, headerHeight);
    if (laneHeaderLabel) {
      // 그리드의 고정 컬럼 헤더처럼 헤더 두 행을 가로질러 가운데에 둔다.
      ctx.font = HEADER_FONT;
      ctx.fillStyle = HEADER_TEXT_COLOR;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(laneHeaderLabel, px(laneLeft / 2), px(headerHeight / 2), laneLeft - 12);
    }
    ctx.textAlign = "left";

    // 외곽선 — 그리드 루트 테두리와 같은 역할. 데이터가 없을 때 특히 이게 없으면 영역이 안 보인다.
    ctx.fillStyle = BORDER_COLOR;
    ctx.fillRect(0, 0, width, 1);
    ctx.fillRect(0, px(height) - 1, width, 1);
    ctx.fillRect(0, 0, 1, height);
    ctx.fillRect(px(width) - 1, 0, 1, height);
  }, [cfg, resources, bucketsByResource, bucketKeys, bucketLabels, colorMode, stackDir, setupColor, showSetup, weekdayLabels, colorForItem, loadColorFn, laneHeaderLabel, emptyLabel, monthLabels, selected, isolateOrderNo, idxOf, horizMetrics, resolveLaneRef]);

  // 스크롤: rAF로 코얼레싱해 다시 그린다.
  const scheduleDraw = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      draw();
    });
  }, [draw]);

  // 좌표(CSS px) → 클릭된 세그먼트 해석. draw()와 동일한 스케일/레이아웃을 재구성.
  // 클릭은 단일 지점이라 좌표 계산으로 충분(버킷 수가 적음). 대용량 우클릭 시에는
  // 오프스크린 color-picking으로 교체 가능(스펙 참고).
  // 좌표 → 리소스/버킷 해석(세그먼트 무관). 우클릭 줌은 버킷 단위라 세그먼트가
  // 없는 빈 영역에서도 동작해야 한다.
  const resolveBucket = useCallback(
    (mx, my) => {
      const scroller = scrollRef.current;
      if (!scroller) return null;
      const { headerHeight, rowHeight, laneLeft } = cfg;

      if (my < headerHeight || mx < laneLeft) return null; // 헤더/좌측 라벨 영역

      const scrollTop = scroller.scrollTop;
      const r = Math.floor((my - headerHeight + scrollTop) / rowHeight);
      if (r < 0 || r >= resources.length) return null;

      const res = resources[r];
      const buckets = bucketsByResource[res.id] || [];

      // 패닝/고정 폭 기준으로 버킷 인덱스 역산
      const { bucketW } = horizMetrics();
      const i = Math.floor((mx - laneLeft + panRef.current) / bucketW);
      if (i < 0 || i >= bucketKeys.length) return null;
      // 응답은 데이터 없는 버킷을 생략한다. 우클릭 메뉴(포커스)는 리소스 단위 동작이라
      // 빈 버킷에서도 열려야 하므로 bucket 없이(null) 리소스 정보만 돌려준다.
      const bucket = buckets.find((b) => b.key === bucketKeys[i]) ?? null;

      const rowTopAbs = headerHeight + r * rowHeight - scrollTop;
      const barLeft = laneLeft + i * bucketW - panRef.current + cfg.bucketGap / 2;
      const barW = bucketW - cfg.bucketGap;
      return { resource: res, buckets, bucket, rowTopAbs, barLeft, barW };
    },
    [cfg, resources, bucketsByResource, bucketKeys, horizMetrics]
  );

  // 위 버킷 해석 + 용량 좌표로 세그먼트(주문)까지 확정.
  const hitTest = useCallback(
    (mx, my) => {
      const base = resolveBucket(mx, my);
      if (!base) return null;
      const { rowPaddingY, rowHeight } = cfg;
      const laneRef = resolveLaneRef(base.buckets, base.resource.maxCapacity);

      // 클릭 지점의 용량 값 — 스택 방향에 따라 축이 다르다.
      let capValue;
      if (stackDir === "horizontal") {
        capValue = base.barW ? ((mx - base.barLeft) / base.barW) * laneRef : -1;
      } else {
        const y = buildCapacityScale(laneRef, base.rowTopAbs + rowPaddingY, base.rowTopAbs + rowHeight - rowPaddingY);
        capValue = y.invert(my);
      }
       if (capValue < 0) return null;

       const stacked = stackBucket(base.bucket?.items ?? []);
       const seg = stacked.find((s) => capValue >= s.y0 && capValue < s.y1);
       if (!seg) return null; // 빈(여유)/cap 라인 밖 영역 클릭

      return { resource: base.resource, bucket: base.bucket, item: seg };
    },
    [resolveBucket, cfg, stackDir, resolveLaneRef]
  );

  // 좌우 드래그로 가로 패닝(스크롤바 없이). 일정 거리 이상 끌면 클릭(선택)은 무시.
  const handleMouseDown = useCallback((e) => {
    dragRef.current = { active: true, startX: e.clientX, startPan: panRef.current, moved: false };
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d.active) return;
      const { maxPan } = horizMetrics();
      const dx = d.startX - e.clientX; // 왼쪽으로 끌면 dx>0 → 뒤 버킷으로 이동
      if (Math.abs(dx) > 4) d.moved = true;
      panRef.current = Math.round(Math.min(Math.max(0, d.startPan + dx), maxPan));
      scheduleDraw();
    },
    [horizMetrics, scheduleDraw]
  );

  const endDrag = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  // Shift + Wheel: 세로 휠 입력을 가로 패닝으로 매핑한다.
  const handleWheel = useCallback(
    (e) => {
      if (!e.shiftKey) return;
      e.preventDefault();

      const { maxPan } = horizMetrics();
      const nextPan = Math.round(Math.min(Math.max(0, panRef.current + e.deltaY), maxPan));
      if (nextPan === panRef.current) return;

      panRef.current = nextPan;
      scheduleDraw();
    },
    [horizMetrics, scheduleDraw]
  );

  const handleClick = useCallback(
    (e) => {
      if (dragRef.current.moved) {
        dragRef.current.moved = false;
        return; // 드래그였으면 선택하지 않음
      }
      if (!onSegmentClick) return;
      const rect = containerRef.current.getBoundingClientRect();
      const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
      if (hit) onSegmentClick(hit); // 세그먼트(주문)만 패널 오픈, 빈 영역 클릭은 무시
    },
    [hitTest, onSegmentClick]
  );

  const handleContextMenu = useCallback(
    (e) => {
      if (!onBucketContextMenu) return;
      const rect = containerRef.current.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = hitTest(mx, my); // 세그먼트(주문) 위면 주문까지 해석
      const base = hit || resolveBucket(mx, my); // 아니면 버킷 단위
      if (!base) return; // 차트 밖이면 기본(브라우저) 메뉴 허용
      e.preventDefault();
      onBucketContextMenu(
        { resource: base.resource, bucket: base.bucket, item: hit ? hit.item : null },
        e.clientX,
        e.clientY
      );
    },
    [hitTest, resolveBucket, onBucketContextMenu]
  );

  // 컨테이너 크기/DPR에 맞춰 캔버스 버퍼 크기를 잡고 다시 그린다.
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = container.clientWidth;
      const height = container.clientHeight;
      sizeRef.current = { width, height, dpr };
      // clientWidth/Height 는 정수라 CSS 크기는 이미 안정적이다. 버퍼만 반올림해 잡고,
      // draw() 가 그 실제 크기로 배율을 되계산한다.
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      draw();
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, [draw]);

  // 버킷 구성이 바뀌면 패닝 초기화
  useEffect(() => {
    panRef.current = 0;
  }, [bucketKeys]);

  // 지정된 리소스·버킷을 화면 안으로 — 패널에서 주문을 고르거나 방향키로 버킷을 옮길 때 쓴다.
  // 이미 보이는 대상은 건드리지 않는다(불필요한 화면 점프 방지).
  useEffect(() => {
    // 요청당 한 번만 처리한다. 이 훅은 resources/cfg 변경에도 다시 도는데, 그때 지난 대상으로
    // 화면이 튀면 안 된다(예: 재조회 직후). 호출 쪽이 매번 새 객체를 넘기므로 동일성으로 판별한다.
    if (!revealTarget || revealedRef.current === revealTarget) return;
    revealedRef.current = revealTarget;

    const { headerHeight, rowHeight } = cfg;
    const { height } = sizeRef.current;
    const scroller = scrollRef.current;

    // 세로 — 해당 리소스 행
    const r = resources.findIndex((res) => res.id === revealTarget.resourceId);
    if (r >= 0 && scroller) {
      const viewH = Math.max(0, height - headerHeight);
      const rowTop = r * rowHeight;
      if (rowTop < scroller.scrollTop || rowTop + rowHeight > scroller.scrollTop + viewH) {
        scroller.scrollTop = Math.max(0, Math.round(rowTop - (viewH - rowHeight) / 2));
      }
    }

    // 가로 — 해당 버킷 열
    const i = idxOf.get(revealTarget.bucketKey);
    if (i != null) {
      const { viewportW, bucketW, maxPan } = horizMetrics();
      const colLeft = i * bucketW; // 콘텐츠 좌표(패닝 전)
      if (colLeft < panRef.current || colLeft + bucketW > panRef.current + viewportW) {
        panRef.current = Math.round(Math.min(Math.max(0, colLeft - (viewportW - bucketW) / 2), maxPan));
      }
    }

    scheduleDraw();
  }, [revealTarget, resources, cfg, idxOf, horizMetrics, scheduleDraw]);

  // 데이터/옵션 변경 시 재렌더
  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />
      {/* 스크롤 캡처용 투명 오버레이 + 전체 높이 스페이서.
          (후속 단계에서 이 레이어로 포인터 좌표 → 캔버스 좌표 히트테스트) */}
      <Box
        ref={scrollRef}
        onScroll={scheduleDraw}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onWheel={handleWheel}
        sx={{
          position: "absolute",
          inset: 0,
          overflowY: "auto",
          overflowX: "hidden",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <Box sx={{ height: totalHeight, width: "1px" }} />
      </Box>
    </Box>
  );
};

export default ResourceGanttCanvas;
